/* buildDynamicSchemas.js */

/**
 * @module buildDynamicSchemas
 *
 * Wraps the static audit-policy schemas with runtime-derived enums so the
 * Ajv validator catches typos in field references that are only knowable
 * once the editor has loaded — concept items / RSI items / command sets /
 * OS list / permission levels.
 *
 * The base shapes live in `schemas/index.js`; this factory clones them and
 * tightens specific properties from `{type: "string"}` to
 * `{type: "string", enum: [...]}` based on the runtime data. Schemas with no
 * runtime-derived enums (`ifExpressionSchema`, `queryBuilderSchema`) are
 * returned by reference unchanged.
 */
const _ = require("lodash");
const {
    auditPolicySchema: baseAuditPolicySchema,
    ruleSchema: baseRuleSchema,
    ifExpressionSchema,
    queryBuilderSchema: baseQueryBuilderSchema,
} = require("../schemas");

// Canonical permission-level values. Used as a floor for the enum so we
// always flag actual typos even when the caller passes an empty / partial
// `permLevels` list — the form's `_getPermLevels()` filters by the current
// user's authorized actions, which is the wrong semantics for a JSON
// edit-time syntactic check (save-time authorization is enforced server
// side anyway).
const CANONICAL_PERMISSION_LEVELS = ["auditManageLevel1", "auditManageLevel2", "auditManageLevel3"];

// Build an enum schema from a set of string values (undefined when none).
//   nullable — also accept JSON null, for optional reference fields the form
//              clears to null (e.g. conceptItemId). permissionLevel uses this
//              too: required, but may be null in legacy data.
//   emptyOk  — also accept "", the form's "unset" for some fields (e.g. os).
// A bogus non-empty string is still rejected in every case.
const _enumSchema = (values, {nullable = false, emptyOk = false} = {}) => {
    const cleaned = _.uniq(_.filter(values, _.isString));
    if (!cleaned.length) {
        return undefined;
    }
    const stringSchema = {type: "string", enum: emptyOk ? [...cleaned, ""] : cleaned};
    return nullable ? {anyOf: [{type: "null"}, stringSchema]} : stringSchema;
};

const buildDynamicSchemas = ({
    labelMaps = {},
    osMap = {},
    permLevels = [],
    qbColumns = [],
} = {}) => {
    // The JSON view keeps the original property names and shows the *label*
    // as the value (ruleLabelMapper converts UUID↔label in place), so every
    // reference enum below is built from the label set, not the UUID set.
    const conceptItemLabels = _.map(
        _.values(labelMaps.conceptItems || {}),
        (entry) => entry && entry.label
    );
    const rsiLabels = _.map(_.values(labelMaps.rsiIdsMap || {}), (entry) => entry && entry.label);
    const commandSetLabels = _.values(labelMaps.commandSets || {});
    const osKeys = ["all", ...Object.keys(osMap || {})];

    // Build enum schemas only when we actually have values to enumerate.
    // permissionLevel is required, so it stays a strict string enum; the
    // reference fields are optional and use the null/""-tolerant variant.
    // Command-set sentinels (all/allConfig/allOperational + their labels) are
    // already merged into labelMaps.commandSets, so they fall out of the
    // values here automatically.
    const permissionLevelEnum = _enumSchema([...CANONICAL_PERMISSION_LEVELS, ...permLevels], {
        nullable: true,
    });
    const optional = {nullable: true, emptyOk: true};
    const osEnum = _enumSchema(osKeys, optional);
    const commandSetEnum = _enumSchema(commandSetLabels, optional);
    const conceptItemEnum = _enumSchema(conceptItemLabels, optional);
    const rsiEnum = _enumSchema(rsiLabels, optional);

    // Top-level policy: tighten permissionLevel.
    const auditPolicySchema = _.cloneDeep(baseAuditPolicySchema);
    if (permissionLevelEnum) {
        auditPolicySchema.properties.permissionLevel = permissionLevelEnum;
    }

    // Rule: tighten os and the (label-valued, name-preserved) reference fields
    // commandSet / conceptItemId / rsiId, plus the same inner fields on
    // commandSetResult.
    const ruleSchema = _.cloneDeep(baseRuleSchema);
    const ruleProps = ruleSchema.properties;
    if (osEnum) {
        ruleProps.os = osEnum;
    }
    if (commandSetEnum) {
        ruleProps.commandSet = commandSetEnum;
    }
    if (conceptItemEnum) {
        ruleProps.conceptItemId = conceptItemEnum;
    }
    if (rsiEnum) {
        ruleProps.rsiId = rsiEnum;
    }

    // commandSetResult is `{anyOf: [{type:null}, {properties:{...}}]}` (the
    // null-tolerant wrapper), so the object shape with `properties` lives in
    // the anyOf branch — reach into it rather than the wrapper itself.
    const csr = ruleProps.commandSetResult;
    const csrSchema = csr && (csr.properties ? csr : _.find(csr.anyOf, "properties"));
    if (csrSchema) {
        const csrProps = csrSchema.properties;
        if (conceptItemEnum) {
            csrProps.conceptItemId = conceptItemEnum;
        }
        if (rsiEnum) {
            csrProps.restSyncItemId = rsiEnum;
        }
    }

    // queryBuilderSchema: tighten leaf.id / leaf.field to the live column-name
    // enum, leaf.operator to the union of operators, and leaf.type / leaf.input
    // to the column-derived value sets — otherwise these bookkeeping fields
    // accept any string and bad values slip through to save.
    // customFieldValues.* paths are excluded from these constraints —
    // they're validated by customFieldEnumValidator instead. Per-id operator
    // checks (operator valid for some column but not the one referenced) are
    // handled by queryBuilderLeafValidator — Ajv enums are global, not
    // conditional on the specific `id` value.
    const qbNameEnum = _enumSchema(_.map(qbColumns, "name"));
    const qbOperatorEnum = _enumSchema(_.uniq(_.flatMap(qbColumns, (c) => c.operators || [])));
    const qbTypeEnum = _enumSchema(_.map(qbColumns, "type"));
    const qbInputEnum = _enumSchema(_.map(qbColumns, "input"));
    let queryBuilderSchema = baseQueryBuilderSchema;
    if (qbNameEnum || qbOperatorEnum) {
        queryBuilderSchema = _.cloneDeep(baseQueryBuilderSchema);
        const leafProps = {};
        if (qbNameEnum) {
            leafProps.id = qbNameEnum;
            leafProps.field = qbNameEnum;
        }
        if (qbOperatorEnum) {
            leafProps.operator = qbOperatorEnum;
        }
        if (qbTypeEnum) {
            leafProps.type = qbTypeEnum;
        }
        if (qbInputEnum) {
            leafProps.input = qbInputEnum;
        }
        // Apply the enum tightening only when `id` does not look like a
        // `customFieldValues.<key>` path.
        queryBuilderSchema.definitions.leaf.allOf = [
            {
                if: {
                    type: "object",
                    required: ["id"],
                    properties: {
                        id: {type: "string", not: {pattern: "^customFieldValues\\."}},
                    },
                },
                then: {properties: leafProps},
            },
        ];
    }

    return {
        auditPolicySchema,
        ruleSchema,
        ifExpressionSchema,
        queryBuilderSchema,
    };
};

module.exports = {buildDynamicSchemas};
